import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerAvatar from './CustomerAvatar';
import type { Customer } from '../hooks/useCustomers';

interface CustomerCardProps {
  customer: Customer;
}

export default function CustomerCard({ customer }: CustomerCardProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (customer.phone) window.open(`tel:${customer.phone}`);
  };

  const handleEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (customer.email) window.open(`mailto:${customer.email}`);
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!customer.phone) return;
    try {
      await navigator.clipboard.writeText(customer.phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/customers/${customer.id}?edit=1`);
  };

  return (
    <div
      onClick={() => navigate(`/customers/${customer.id}`)}
      className="bg-white rounded-xl border border-gray-100 cursor-pointer hover:border-teal-200 transition-all group relative overflow-hidden flex flex-col"
    >
      {/* Main content */}
      <div className="p-5 flex-1">
        {/* Top section: avatar + name + type */}
        <div className="flex items-start gap-3 mb-4">
          <CustomerAvatar
            name={customer.fullName}
            avatarUrl={customer.avatar}
            size="md"
            className="ring-2 ring-amber-400"
          />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate leading-tight">
              {customer.fullName || '未命名客户'}
              {/* 辅助称呼：该客户注册升格时姓名与原档案有出入，系统自动生成（仅客户页显示） */}
              {customer.displayName && customer.displayName !== customer.fullName && (
                <span className="ml-1 text-xs font-normal text-slate-400">（称呼：{customer.displayName}）</span>
              )}
            </p>
            {customer.companyName && (
              <p className="text-xs text-gray-400 truncate mt-0.5">{customer.companyName}</p>
            )}
          </div>
          <div className="w-5 h-5 flex items-center justify-center text-gray-300 group-hover:text-teal-400 transition-colors">
            <i className="ri-arrow-right-s-line text-base" />
          </div>
        </div>

        {/* 身份徽标：已注册用户（绿）/ 非注册用户（粉）。客户类型不在这里——它和标签同属分类信息，摆在下方一行 */}
        {customer.isUser !== undefined && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {customer.isUser === true ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs rounded-full border border-emerald-100">
                <i className="ri-shield-check-line text-xs" />
                已注册用户
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-50 text-rose-600 text-xs rounded-full border border-rose-100">
                <i className="ri-user-line text-xs" />
                非注册用户
              </span>
            )}
          </div>
        )}

        {/* Contact info */}
        <div className="space-y-1.5 mb-4">
          {customer.phone && (
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                <i className="ri-phone-line text-xs text-gray-300" />
              </div>
              <span className="text-xs text-gray-500">{customer.phone}</span>
            </div>
          )}
          {customer.email && (
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                <i className="ri-mail-line text-xs text-gray-300" />
              </div>
              <span className="text-xs text-gray-500 truncate">{customer.email}</span>
            </div>
          )}
          {customer.location && (
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                <i className="ri-map-pin-line text-xs text-gray-300" />
              </div>
              <span className="text-xs text-gray-500 truncate">{customer.location}</span>
            </div>
          )}
          {!customer.phone && !customer.email && !customer.location && (
            <p className="text-xs text-gray-300 italic">暂无联系方式</p>
          )}
        </div>

        {/* 客户类型 + 标签同一行，类型打头（对齐 Flutter 客户卡与 web 详情页） */}
        {(customer.customerTypeName || (customer.tags?.length ?? 0) > 0) && (
          <div className="flex flex-wrap gap-1.5 pt-3 border-t border-gray-200">
            {customer.customerTypeName && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-600 text-xs rounded-full border border-teal-100">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-user-star-line text-xs" />
                </div>
                {customer.customerTypeName}
              </span>
            )}
            {(customer.tags ?? []).map((tag) => (
              <span
                key={tag.id}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
                style={{
                  backgroundColor: `${tag.color ?? '#14b8a6'}18`,
                  color: tag.color ?? '#14b8a6',
                  border: `1px solid ${tag.color ?? '#14b8a6'}30`,
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: tag.color ?? '#14b8a6' }}
                />
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Quick action bar — slides in on hover */}
      <div className="h-0 group-hover:h-11 overflow-hidden transition-all duration-200 ease-in-out">
        <div className="flex items-center border-t border-gray-200">
          {/* Call */}
          <ActionBtn
            icon="ri-phone-line"
            label="拨打电话"
            onClick={handleCall}
            disabled={!customer.phone}
          />
          {/* Email */}
          <ActionBtn
            icon="ri-mail-send-line"
            label="发送邮件"
            onClick={handleEmail}
            disabled={!customer.email}
          />
          {/* Copy phone */}
          <ActionBtn
            icon={copied ? 'ri-check-line' : 'ri-file-copy-line'}
            label={copied ? '已复制' : '复制号码'}
            onClick={handleCopy}
            disabled={!customer.phone}
            active={copied}
          />
          {/* Edit */}
          <ActionBtn
            icon="ri-edit-line"
            label="编辑资料"
            onClick={handleEdit}
          />
        </div>
      </div>
    </div>
  );
}

interface ActionBtnProps {
  icon: string;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  active?: boolean;
}

function ActionBtn({ icon, label, onClick, disabled = false, active = false }: ActionBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`flex-1 min-h-11 px-1 flex flex-col items-center justify-center gap-0.5 transition-colors cursor-pointer
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-700
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-teal-50'}
        ${active ? 'text-teal-700' : 'text-gray-500 hover:text-teal-700'}
      `}
    >
      <div className="w-4 h-4 flex items-center justify-center">
        <i className={`${icon} text-sm`} />
      </div>
      {/* text-[10px] 是写死的 px，字号设置切到「大」也不会跟着涨，所以用 text-xs 走 rem 档 */}
      <span className="text-xs leading-tight">{label}</span>
    </button>
  );
}
